"use client";

import { useEffect, useState } from "react";
import { useExperiment } from "statsig-react";

/** `?quickSubscribe=1` (or `=0`) forces an arm outside production. */
const URL_OVERRIDE_PARAM = "quickSubscribe";

/**
 * The one home for the `search-discover-quick-subscribe` experiment identity
 * (name + param + default) so the search overlay and the discover page cannot
 * drift to different spellings — a typo at either call site would silently
 * drop that surface to control. Call it only where the treatment surface
 * actually renders: `useExperiment` logs an exposure at the call site (see
 * docs/specs/statsig.md → "Active experiments").
 *
 * Outside production, `?quickSubscribe=1` forces treatment and `=0` forces
 * control, so the arms are reviewable on a preview deploy without a Statsig
 * override. The param is read in an effect (not during render) to keep the
 * first client render identical to the server's, and reads the NEXT_PUBLIC_
 * env var because a bare `VERCEL_ENV` is undefined in the browser — which
 * would leave the escape hatch live in production.
 */
export function useQuickSubscribeExperiment(): boolean {
  const experiment = useExperiment("search-discover-quick-subscribe");
  const assignedValue = experiment.config.get("enabled", false);
  const [urlOverride, setUrlOverride] = useState<boolean | null>(null);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") return;
    const param = new URLSearchParams(window.location.search).get(
      URL_OVERRIDE_PARAM
    );
    if (param === null) return;
    setUrlOverride(param === "1" || param === "true");
  }, []);

  return urlOverride ?? assignedValue;
}
